/**
 * AutoEmbedQueue - Background queue for auto-generating embeddings
 *
 * Automatically generates embeddings when objects change.
 * Uses debouncing, content hashing, and sequential processing
 * to efficiently keep embeddings up-to-date without blocking the UI.
 *
 * No-op when embeddings service is not configured (no API key).
 */

import { embeddingsService } from './embeddingsService';

interface QueueEntry {
  objectId: string;
  enqueuedAt: number;
  timer: ReturnType<typeof setTimeout>;
}

const DEBOUNCE_MS = 2000; // Wait 2s after last change before embedding
const PROCESS_INTERVAL_MS = 200; // 200ms between API calls

class AutoEmbedQueue {
  private queue: Map<string, QueueEntry> = new Map();
  private processing = false;
  private pendingProcess: string[] = []; // objectIds ready to process
  private started = false;
  private contentHashes: Map<string, string> = new Map();

  /**
   * Start the queue. Call once on app init.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    console.log('[AutoEmbedQueue] Started (configured:', embeddingsService.isConfigured(), ')');
  }

  /**
   * Stop the queue and clear all pending items.
   */
  stop(): void {
    this.started = false;
    for (const entry of this.queue.values()) {
      clearTimeout(entry.timer);
    }
    this.queue.clear();
    this.pendingProcess = [];
    console.log('[AutoEmbedQueue] Stopped');
  }

  /**
   * Enqueue an object for embedding. Debounces by objectId.
   * Call this after DataRepository saves.
   */
  enqueue(objectId: string): void {
    if (!this.started) return;
    if (!embeddingsService.isConfigured()) return;

    // Clear existing debounce timer for this object
    const existing = this.queue.get(objectId);
    if (existing) {
      clearTimeout(existing.timer);
    }

    // Set new debounce timer
    const timer = setTimeout(() => {
      this.queue.delete(objectId);
      this.pendingProcess.push(objectId);
      this.processNext();
    }, DEBOUNCE_MS);

    this.queue.set(objectId, {
      objectId,
      enqueuedAt: Date.now(),
      timer,
    });
  }

  /**
   * Process the next item in the pending queue.
   * Sequential processing with rate limiting.
   */
  private async processNext(): Promise<void> {
    if (this.processing) return;
    if (this.pendingProcess.length === 0) return;

    this.processing = true;

    while (this.pendingProcess.length > 0) {
      const objectId = this.pendingProcess.shift()!;

      try {
        await this.embedObject(objectId);
      } catch (error) {
        console.error('[AutoEmbedQueue] Failed to embed:', objectId, error);
        // Don't re-queue on failure — next edit will re-trigger
      }

      // Rate limit between API calls
      if (this.pendingProcess.length > 0) {
        await new Promise(resolve => setTimeout(resolve, PROCESS_INTERVAL_MS));
      }
    }

    this.processing = false;
  }

  /**
   * Generate embedding for a single object.
   * Skips if content hash hasn't changed.
   */
  private async embedObject(objectId: string): Promise<void> {
    if (!embeddingsService.isConfigured()) return;

    // Dynamic import to avoid circular dependency
    const { dataRepository } = await import('../../repository/DataRepository');

    const object = await dataRepository.getObject(objectId);
    if (!object || object.deletedAt) return;

    // Build text content: name + block contents
    const blocks = await dataRepository.getBlocksForObject(objectId);
    const textBlocks = blocks.filter(b =>
      !b.deletedAt &&
      ['text', 'heading', 'heading1', 'heading2', 'heading3', 'list', 'checklist', 'tasklist', 'code', 'callout'].includes(b.type)
    );

    const textContent = [
      object.name,
      object.tags?.join(' ') || '',
      ...textBlocks.map(b => stripHtml(b.content || ''))
    ].filter(Boolean).join('\n');

    if (!textContent.trim()) return;

    // Content hash check — skip if unchanged
    const hash = simpleHash(textContent.substring(0, 500));
    const previousHash = this.contentHashes.get(objectId);
    if (previousHash === hash) {
      return; // Content hasn't changed
    }

    // Generate and cache embedding
    const snippet = textContent.substring(0, 500);
    const textToStore = `HASH:${hash}|${snippet}`;

    try {
      const vector = await embeddingsService.generateEmbedding(textContent);
      await embeddingsService.cacheEmbedding(objectId, vector, textToStore);
      this.contentHashes.set(objectId, hash);
      console.log('[AutoEmbedQueue] Embedded:', object.name?.substring(0, 40));
    } catch (error) {
      // API errors (rate limit, etc.) — don't update hash so we retry next time
      throw error;
    }
  }
}

/**
 * Simple HTML tag stripping for block content.
 */
function stripHtml(html: string): string {
  if (!html) return '';
  // Remove HTML tags, decode common entities
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Simple string hash using btoa for content change detection.
 */
function simpleHash(text: string): string {
  try {
    // Use a simple FNV-1a-like hash for speed
    let hash = 2166136261;
    for (let i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = (hash * 16777619) >>> 0;
    }
    return hash.toString(36);
  } catch {
    return '0';
  }
}

// Export singleton
export const autoEmbedQueue = new AutoEmbedQueue();
